<template>
  <div class="aroma-report-detail">
    <aroma-secondary-nav title="报告详情" fallback="AromaReportList" />

    <div v-if="loading" class="aroma-report-detail__state">
      <van-loading color="#1a6bb0" size="28">正在加载报告</van-loading>
    </div>

    <div v-else-if="errorMessage" class="aroma-report-detail__state">
      <van-empty :description="errorMessage"></van-empty>
      <van-button type="info" size="small" @click="initPage">重新加载</van-button>
    </div>

    <main v-else-if="report" class="aroma-report-detail__card">
      <header class="report-title">
        <h1>睡眠分析报告</h1>
        <p>监测日期：{{ formatReportDate(report.reportDate) }}</p>
      </header>

      <section class="report-section basic-info-section">
        <h2>基本信息</h2>
        <dl class="basic-info" aria-label="基本信息">
          <div class="basic-info__item">
            <dt>设备ID</dt>
            <dd>{{ report.sn || '--' }}</dd>
          </div>
          <div class="basic-info__item">
            <dt>姓名</dt>
            <dd>{{ formatProfileValue(profile && profile.realName) }}</dd>
          </div>
          <div class="basic-info__item">
            <dt>性别</dt>
            <dd>{{ genderText }}</dd>
          </div>
          <div class="basic-info__item">
            <dt>年龄</dt>
            <dd>{{ ageText }}</dd>
          </div>
          <div class="basic-info__item">
            <dt>身高</dt>
            <dd>{{ measurementText(profile && profile.height, 'cm') }}</dd>
          </div>
          <div class="basic-info__item">
            <dt>体重</dt>
            <dd>{{ measurementText(profile && profile.weight, 'kg') }}</dd>
          </div>
        </dl>
      </section>

      <section class="report-section">
        <h2>睡眠分期</h2>
        <aroma-sleep-stage-chart :series="sleepStageSeries"></aroma-sleep-stage-chart>
      </section>

      <section class="report-section">
        <h2>心率和呼吸趋势</h2>
        <aroma-vital-chart :series="vitalSeries"></aroma-vital-chart>
      </section>

      <section v-for="section in reportSections" :key="section.title" class="report-section">
        <h2>{{ section.title }}</h2>
        <div class="metric-table" role="table" :aria-label="section.title">
          <div class="metric-row metric-row--header" role="row">
            <span role="columnheader">项目</span>
            <span role="columnheader">监测结果</span>
          </div>
          <div v-for="row in section.rows" :key="row.name" class="metric-row" role="row">
            <span class="metric-name" role="cell">
              <span>{{ row.name }}</span>
              <button
                class="metric-help"
                type="button"
                :aria-label="`${row.name}参考范围`"
                @click="showReference(row)"
              >
                <van-icon name="question-o"></van-icon>
              </button>
            </span>
            <strong class="metric-value" role="cell">
              <span>{{ row.value }}</span>
              <span
                v-if="row.status"
                class="metric-status"
                :class="`metric-status--${row.status}`"
                :aria-label="metricStatusLabel(row)"
              >{{ metricStatusSymbol(row.status) }}</span>
            </strong>
          </div>
        </div>
      </section>
    </main>

    <div v-if="helpVisible" class="help-overlay" role="presentation" @click.self="closeReference">
      <section class="help-dialog" role="dialog" aria-modal="true" aria-labelledby="help-dialog-title">
        <header class="help-dialog__header">
          <h2 id="help-dialog-title">帮助信息</h2>
          <button type="button" class="help-dialog__close" aria-label="关闭帮助信息" @click="closeReference">
            <van-icon name="cross"></van-icon>
          </button>
        </header>
        <div class="help-dialog__content">
          <span class="help-dialog__info" aria-hidden="true">i</span>
          <div>
            <p class="help-dialog__metric">{{ helpRow && helpRow.name }}</p>
            <p class="help-dialog__reference">{{ helpRow && helpRow.reference }}</p>
          </div>
        </div>
        <button type="button" class="help-dialog__confirm" @click="closeReference">我知道了</button>
      </section>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { Icon, Loading, NavBar } from 'vant'

import API from '@/api'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import AromaSleepStageChart from '@/components/aroma-report/AromaSleepStageChart.vue'
import AromaVitalChart from '@/components/aroma-report/AromaVitalChart.vue'
import { buildReportSections, extractSleepStageSeries, extractVitalSeries } from '@/utils/aroma-report-detail'
import { clearSniSession, getSniSession, isValidSniSn, saveSniSession } from '@/utils/sni-session'

export default {
  name: 'AromaReportDetail',

  components: {
    AromaSecondaryNav,
    AromaSleepStageChart,
    AromaVitalChart,
    [Icon.name]: Icon,
    [Loading.name]: Loading,
    [NavBar.name]: NavBar
  },

  data () {
    return {
      loading: true,
      errorMessage: '',
      report: null,
      profile: null,
      helpVisible: false,
      helpRow: null
    }
  },

  computed: {
    sn () {
      return this.$route.query.sn || ''
    },

    reportId () {
      return Number(this.$route.query.reportId)
    },

    session () {
      return getSniSession(this.sn)
    },

    token () {
      return this.session && this.session.token
    },

    isWechat () {
      return typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
    },

    reportSections () {
      return this.report ? buildReportSections(this.report) : []
    },

    vitalSeries () {
      return this.report ? extractVitalSeries(this.report) : null
    },

    sleepStageSeries () {
      return this.report ? extractSleepStageSeries(this.report) : null
    },

    genderText () {
      if (!this.profile) return '--'
      if (this.profile.sex === '0') return '男'
      if (this.profile.sex === '1') return '女'
      return '--'
    },

    ageText () {
      if (!this.profile || !this.profile.birthday) return '--'
      const birthday = dayjs(this.profile.birthday)
      return birthday.isValid() ? `${dayjs().diff(birthday, 'year')}岁` : '--'
    }
  },

  mounted () {
    this.initPage()
  },

  methods: {
    async initPage () {
      if (!isValidSniSn(this.sn)) {
        this.loading = false
        this.errorMessage = '设备 SN 为空'
        return
      }
      if (!Number.isInteger(this.reportId) || this.reportId <= 0) {
        this.loading = false
        this.errorMessage = '报告编号不正确'
        return
      }
      if (!this.token) {
        this.navigateToLogin()
        return
      }
      this.loading = true
      this.errorMessage = ''
      try {
        const [profile, report] = await Promise.all([
          API.sniCurrent(this.token),
          API.sniReportDetail({ sn: this.sn, reportId: this.reportId }, this.token)
        ])
        if (!profile.profileCompleted) {
          this.$router.replace({ name: 'AromaReportProfile', query: { sn: this.sn } })
          return
        }
        this.profile = profile
        this.report = report
        saveSniSession(this.sn, {
          ...this.session,
          profileCompleted: profile.profileCompleted
        })
      } catch (error) {
        if (error && (error.code === '8' || error.code === '11')) {
          clearSniSession(this.sn)
          this.navigateToLogin()
          return
        }
        this.errorMessage = (error && error.message) || '报告加载失败'
      } finally {
        this.loading = false
      }
    },

    navigateToLogin () {
      this.$router.replace({ name: 'AromaReportLogin', query: { sn: this.sn } })
    },

    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace({ name: 'AromaReportList', query: { sn: this.sn } })
      }
    },

    showReference (row) {
      this.helpRow = row
      this.helpVisible = true
    },

    closeReference () {
      this.helpVisible = false
      this.helpRow = null
    },

    formatReportDate (value) {
      if (!value || value.length < 8) return '--'
      return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
    },

    formatProfileValue (value) {
      return value === undefined || value === null || String(value).trim() === '' ? '--' : value
    },

    measurementText (value, unit) {
      const text = this.formatProfileValue(value)
      return text === '--' ? text : `${text} ${unit}`
    },

    metricStatusSymbol (status) {
      if (status === 'normal') return '✓'
      if (status === 'high') return '↑'
      if (status === 'low') return '↓'
      return ''
    },

    metricStatusLabel (row) {
      if (!row) return ''
      if (row.status === 'normal') return `${row.name}在参考范围内`
      if (row.status === 'high') return `${row.name}高于参考范围`
      if (row.status === 'low') return `${row.name}低于参考范围`
      return ''
    }
  }
}
</script>

<style scoped lang="scss">
.aroma-report-detail {
  width: 100%;
  min-height: 100%;
  padding: 12px 12px calc(24px + env(safe-area-inset-bottom));
  background: linear-gradient(145deg, #0a2a5a 0%, #1a5a8a 100%);
  box-sizing: border-box;

  ::v-deep .van-nav-bar {
    background: #ffffff;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #1a6bb0;
  }

  &__state {
    display: flex;
    min-height: 360px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: 12px;
    border-radius: 8px;
    background: #ffffff;
  }

  &__card {
    width: 100%;
    max-width: 720px;
    margin: 12px auto 0;
    padding: 18px 12px 24px;
    border: 1px solid rgba(255, 255, 255, 0.36);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 16px 40px rgba(0, 10, 30, 0.28);
    box-sizing: border-box;
  }
}

.report-title {
  padding-bottom: 12px;
  border-bottom: 2px solid #c7dfff;
  text-align: center;

  h1 {
    margin: 0;
    color: #1a3f6a;
    font-size: 22px;
    font-weight: 600;
    line-height: 30px;
  }

  p {
    margin: 4px 0 0;
    color: #4a6f8e;
    font-size: 13px;
    line-height: 20px;
  }
}

.report-section {
  margin-top: 24px;

  h2 {
    margin: 0 0 10px;
    padding-left: 10px;
    border-left: 4px solid #1a6bb0;
    color: #1a3f6a;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
  }
}

.basic-info-section {
  margin-top: 20px;
}

.basic-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 0;
}

.basic-info__item {
  min-width: 0;
  padding: 11px 12px;
  border: 1px solid #dce8f2;
  border-radius: 8px;
  background: #fff;
}

.basic-info dt {
  color: #718095;
  font-size: 12px;
  line-height: 18px;
}

.basic-info dd {
  margin: 2px 0 0;
  overflow-wrap: anywhere;
  color: #1f2329;
  font-size: 14px;
  font-weight: 600;
  line-height: 21px;
}

.metric-table {
  overflow: hidden;
  border: 1px solid #dce8f2;
  border-radius: 8px;
}

.metric-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  min-height: 44px;

  & + & {
    border-top: 1px solid #edf0f5;
  }

  > span,
  > strong {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    padding: 7px 8px;
    text-align: center;
    overflow-wrap: anywhere;
    box-sizing: border-box;
  }

  > strong {
    border-left: 1px solid #edf0f5;
    color: #1f2329;
    font-size: 13px;
    font-weight: 500;
  }

  &--header {
    color: #1a3f6a;
    background: #d6e8fa;
    font-size: 13px;
    font-weight: 600;

    > span + span {
      border-left: 1px solid #aac3df;
    }
  }
}

.metric-value {
  gap: 6px;
}

.metric-status {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.metric-status--normal {
  color: #047d14;
}

.metric-status--high,
.metric-status--low {
  color: #ff1f10;
}

.metric-name {
  gap: 2px;
  color: #1f2329;
  font-size: 13px;
}

.metric-help {
  display: inline-grid;
  flex: none;
  width: 28px;
  height: 28px;
  margin: -4px -4px -4px 0;
  padding: 0;
  place-items: center;
  border: 0;
  color: #1a6bb0;
  background: transparent;
  font-size: 16px;
}

.help-overlay {
  position: fixed;
  z-index: 2000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
  box-sizing: border-box;
}

.help-dialog {
  width: min(100%, 560px);
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 16px 50px rgba(0, 0, 0, 0.26);
}

.help-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 26px 14px;
}

.help-dialog__header h2 {
  margin: 0;
  color: #343940;
  font-size: 22px;
  line-height: 30px;
}

.help-dialog__close {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 0;
  color: #8f969f;
  background: transparent;
  font-size: 22px;
}

.help-dialog__content {
  display: flex;
  gap: 12px;
  padding: 4px 26px 24px;
  color: #656b73;
  font-size: 16px;
  line-height: 1.7;
}

.help-dialog__info {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: none;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  border-radius: 50%;
  color: #fff;
  background: #9299a3;
  font-family: Georgia, serif;
  font-size: 21px;
  font-style: italic;
  font-weight: 700;
}

.help-dialog__metric,
.help-dialog__reference {
  margin: 0;
}

.help-dialog__metric {
  color: #4c5158;
  font-weight: 600;
}

.help-dialog__confirm {
  display: block;
  width: calc(100% - 52px);
  min-height: 48px;
  margin: 0 26px 24px;
  border: 0;
  border-radius: 28px;
  color: #fff;
  background: #62acf4;
  box-shadow: 0 10px 20px rgba(98, 172, 244, 0.3);
  font-size: 17px;
  font-weight: 600;
}

@media (min-width: 600px) {
  .aroma-report-detail__card {
    padding: 20px 16px 28px;
  }
}

</style>
